<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">{{ tax_data.tax.name_expense }}</h1>
          <app-option-picker
            :options="status_options"
            v-model="tax_data.status"
            @click="onUpdateTax({status: tax_data.status.code})"
          />
        </app-cells>
        <app-button
          v-if="tax_data.invoice"
          @click.native="$modal.show('delete-modal')"
          theme="error"
          size="small"
        >
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Legal entity:</span> <span>{{ tax_data.legal_entity.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span> <span>{{ dateFormat(tax_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Accounting date:</span> <span>{{ dateFormatMonth(tax_data.accounting_date) }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="tax_data.notes"
          rows="5"
          @change.native="onUpdateTax({ notes: tax_data.notes })"
          placeholder="Write a notes"
        />
      </app-cells>
      <app-button
        v-if="tax_data.invoice === null && tax_data.status.code === 'submitted' && tax_data.amount"
        @click.native="onShowInvoiceModal"
      >
        Attach invoice
      </app-button>
      <app-cells>
        <h2 class="title">Details</h2>
        <app-button @click.native="onCheckForm" size="small" ref="submitTax">Save</app-button>
      </app-cells>
      <app-flex-grid :rows="[['200px', '200px',], ['200px', '200px',]]" alignItems="start">
        <template #col-1-1>
          <app-form-group label="Date" required>
            <app-date-picker
              v-model="tax_form.date"
              :clearable="false"
              :readonly="tax_form.invoice"
              placeholder="Date"
              :error="$v.tax_form.date.$error"
            />
            <template #error>
              <div v-if="$v.tax_form.date.$dirty && !$v.tax_form.date.required">Required field</div>
            </template>
          </app-form-group>
        </template>
        <template #col-1-2>
          <app-form-group label="Accounting date" required>
            <app-date-picker
              v-model="tax_form.accounting_date"
              :clearable="false"
              :readonly="tax_form.invoice"
              type="month"
              format="MMM YYYY"
              placeholder="Accounting date"
              :error="$v.tax_form.accounting_date.$error"
            />
            <template #error>
              <div v-if="$v.tax_form.accounting_date.$dirty && !$v.tax_form.accounting_date.required">Required field</div>
            </template>
          </app-form-group>
        </template>
        <template #col-2-1>
          <app-form-field label="Amount" label-for="amount" required>
            <app-fin-number
              v-model="tax_form.amount"
              :readonly="tax_form.invoice"
              id="amount"
              :error="$v.tax_form.amount.$error"
            />
            <template #error>
              <div v-if="$v.tax_form.amount.$dirty && !$v.tax_form.amount.required">Required field</div>
            </template>
          </app-form-field>
        </template>
        <template #col-2-2>
          <app-form-field label="Attachment">
            <app-uploader
              v-model="tax_form.attachment"
              :extensions="['application/pdf']"
              formDataFormats="PDF"
              class="custom-uploader--flex"
            />
          </app-form-field>
        </template>
      </app-flex-grid>
      <template v-if="tax_data.invoice">
        <app-cells><h2 class="title">Invoice</h2></app-cells>
        <v-client-table
          :data="[tax_data.invoice]"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
        >
          <template #h__supplier><div class="table-default__left">Supplier</div></template>
          <template #h__responsible><div class="table-default__left">Responsible</div></template>
          <template #date="{ row }">{{ dateFormat(row.date) }}</template>
          <template #supplier="{ row }">
            <div class="table-default__left">
              <router-link
                :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
                class="table-default__link-inline"
              >
                {{ row.supplier.legal_name }}
              </router-link>
            </div>
          </template>
          <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible?.full_name || '-' }}</div></template>
          <template #amount="{ row }">
            {{ amountFormat(row.amount_total) }} {{ row.currency.code }}
          </template>
          <template #invoice_number="{ row }">{{ row.invoice_number || '-' }}</template>
          <template #payment_method="{ row }">{{ row.supplier_payment_method.payment_method.name || '-' }}</template>
          <template #account="{ row }">
            <template v-if="row.supplier_payment_method.payment_method.code === 'bank'">
              <template v-if="row.supplier_payment_method.bank_account">
                {{ row.supplier_payment_method.bank_account.account_number }}<br>{{ row.supplier_payment_method.bank_account.bank.short_name }}
              </template>
              <template v-else>-</template>
            </template>
            <template v-else-if="row.supplier_payment_method.payment_method.code === 'mobile'">
              <template v-if="row.supplier_payment_method.mobile_account">
                {{ row.supplier_payment_method.mobile_account.account_number }}<br>{{ row.supplier_payment_method.mobile_account.provider.name }}
              </template>
              <template v-else>-</template>
            </template>
            <template v-else-if="row.supplier_payment_method.payment_method.code === 'gepg'">
              <template v-if="row.gepg_number">{{ row.gepg_number }}</template>
              <template v-else>-</template>
            </template>
            <template v-else>-</template>
          </template>
          <template #payment_plans="{ row }">
          <span
            :style="{ 'backgroundColor': row.payment_status === 'unpaid' ? '#e85c20' : row.payment_status === 'paid' ? '#77b017' : '#439ad1' }"
            class="status-tag"
          >
            {{ row.payment_status === 'unpaid' ? 'Unpaid' : row.payment_status === 'paid' ? 'Paid' : amountFormat(row.paid_amount) }}
          </span>
          </template>
          <template #attachment="{ row }">
            <div class="table-default__controls">
              <a
                :href="row.attachment?.file"
                target="_blank"
                :class="{'table-default__disabled': !row.attachment}"
              >
                <img
                  src="@/assets/img/snacks-link.svg"
                  alt="Attachment"
                >
              </a>
            </div>
          </template>
        </v-client-table>
      </template>
    </template>
    <modal
      name="invoice-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('invoice-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Attach invoice</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckInvoiceForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="form.date"
                placeholder="Specify a date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Invoice number" label-for="invoice-number" required>
              <app-input
                v-model="form.invoice_number"
                id="invoice-number"
                name="invoice-number"
                placeholder="Invoice number"
                :error="$v.form.invoice_number.$error"
              />
              <template #error>
                <div v-if="$v.form.invoice_number.$dirty && !$v.form.invoice_number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Attachment">
              <app-uploader
                v-model="form.attachment"
                :extensions="['application/pdf', 'image/jpeg', 'image/png']"
                formDataFormats="PDF,JPEG,PNG"
                class="custom-uploader--flex"
              />
            </app-form-group>
            <app-form-group label="Supplier" required>
              <app-select
                @search="onSupplierSearch"
                @input="onChangeSupplier"
                v-model="form.supplier"
                :options="supplier_options"
                :clearable="false"
                label="trade_name"
                placeholder="Supplier"
                :error="$v.form.supplier.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <template v-if="form.supplier?.verified">
              <app-form-group label="Payment method" required>
                <app-select
                  v-model="form.supplier_payment_method"
                  :options="form.supplier?.payment_methods"
                  :clearable="false"
                  :searchable="false"
                  label="public_name"
                  placeholder="Payment method"
                  :error="$v.form.supplier_payment_method.$error"
                />
                <template #error>
                  <div v-if="$v.form.supplier_payment_method.$dirty && !$v.form.supplier_payment_method.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group
                v-if="form.supplier_payment_method?.payment_method?.code === 'gepg'"
                label="GePG number"
                required label-for="gepg_number"
              >
                <app-input
                  v-model="form.gepg_number"
                  placeholder="GePG number"
                  id="gepg_number"
                  :error="$v.form.gepg_number.$error"
                />
                <template #error>
                  <div v-if="$v.form.gepg_number.$dirty && !$v.form.gepg_number.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group label="Due date" required>
                <app-date-picker
                  v-model="form.due_date"
                  placeholder="Specify a date"
                  :error="$v.form.due_date.$error"
                />
                <template #error>
                  <div v-if="$v.form.due_date.$dirty && !$v.form.due_date.required">Required field</div>
                </template>
              </app-form-group>
            </template>
            <app-form-group>
              <div v-if="form.supplier?.verified === false" class="popup__warning">
                Payment methods have not been verified for this supplier. Please reach out to the accounting
                department to complete the information. After that, you can add the invoice.
              </div>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transaction">Attach</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete tax
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this tax?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteTax" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceTaxes,
  getAdministrativeSuppliers,
  getFinanceTaxes,
  getFinanceTaxStatuses,
  patchFinanceTaxes,
  postFinanceTaxesInvoice
} from '@/http'
import { format, parse } from 'date-fns'
import { required, requiredIf } from 'vuelidate/lib/validators'
import AppOptionPicker from '@/components/AppOptionPicker.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceTaxesDetail',
  components: {AppOptionPicker},
  data() {
    return {
      page_loaded: false,
      tax_data: {},
      status_options: [],
      supplier_options: [],
      form: {},
      tax_form: {},
      table: {
        columns: [
          'date',
          'supplier',
          'amount',
          'invoice_number',
          'responsible',
          'payment_method',
          'account',
          'payment_plans',
          'attachment',
        ],
        options: {
          headings: {
            'date': 'Date',
            'supplier': 'Supplier',
            'amount': 'Amount',
            'invoice_number': 'Invoice number',
            'responsible': 'Responsible',
            'payment_method': 'Payment method',
            'account': 'Account',
            'payment_plans': 'Payment',
            'attachment': 'Attachment',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  validations: {
    tax_form: {
      amount: { required },
      date: { required },
      accounting_date: { required },
    },
    form: {
      date: { required },
      invoice_number: { required },
      supplier: { required },
      supplier_payment_method: { required },
      due_date: { required },
      gepg_number: {
        required: requiredIf(function() {
          return this.form.supplier_payment_method?.payment_method?.code === 'gepg'
        })
      },
    },
  },
  created() {
    this.fetchData()
    getFinanceTaxStatuses().then((response) => { this.status_options = response.data })
  },
  methods: {
    fetchData() {
      getFinanceTaxes(this.$route.params.id)
        .then(response => {
          this.tax_data = response.data
          this.tax_form = {...response.data}
          this.page_loaded = true
        })
    },
    onCheckForm() {
      this.$v.tax_form.$touch()
      if (this.$v.tax_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submitTax.preload = true
      const data = {
        attachment: this.tax_form.attachment?.file ? this.tax_form.attachment.id : null,
        amount: this.tax_form.amount,
        date: this.tax_form.date,
        accounting_date: this.tax_form.accounting_date,
      }
      patchFinanceTaxes(this.$route.params.id, data)
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$refs.submitTax.preload = false
        })
    },
    onDeleteTax() {
      this.$refs.delete.preload = true
      deleteFinanceTaxes(this.$route.params.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Tax deleted',
          })
          this.$router.push({ name: 'finance-taxes' })
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onUpdateTax(data) {
      patchFinanceTaxes(this.$route.params.id, data)
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
        })
    },
    onChangeSupplier(e) {
      this.form.supplier_payment_method = e.payment_methods[0]
    },
    onCheckInvoiceForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendInvoiceForm()
      }
    },
    sendInvoiceForm() {
      this.$refs.submit_transaction.preload = true
      const formData = { ...this.form }
      if (formData.attachment) formData.attachment = formData.attachment.id
      formData.supplier = formData.supplier.id
      formData.supplier_payment_method = formData.supplier_payment_method.id
      postFinanceTaxesInvoice(this.$route.params.id, formData)
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Invoice created',
          })
          this.$modal.hide('invoice-modal')
        })
        .catch(() => { this.$refs.submit_transaction.preload = false })
    },
    onShowInvoiceModal() {
      this.form = {
        date: this.tax_data.date,
        supplier_payment_method: null
      }
      this.$v.form.$reset()
      this.$modal.show('invoice-modal')
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return new Intl.NumberFormat('ru-RU').format(amount)
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  },
}
</script>

